import {Helmet} from '@dr.pogodin/react-helmet';
import {DataStatus} from 'platform/components';

import {
  useGetRentalVehicleQuery,
  useGetVehicleQuery,
  usePutRentalVehicleMutation,
} from '@dms/api/vehicle';
import {
  RentalVehicle,
  RentalVehicleValidationSchema,
  toBaseVehicle,
  useVehicleFormContext,
  VehicleForm as VFKit,
} from '@dms/features/vehicle-forms-kit';
import i18n from '@dms/i18n';
import {vehiclesRoutes} from '@dms/routes';
import {formatDocumentTitle, FullScreenModal, handleApiError} from '@dms/shared';

import {composePath, useNavigate, useRequiredParams} from 'shared/web';

import {RentalInformation} from '../(sections)/RentalInformation';

interface EditRentalVehicleFormProps {
  isUpdating: boolean;
  onSave: (values: RentalVehicle) => void;
  onDiscard: () => void;
}

const EditRentalVehicleForm = ({onSave, onDiscard, isUpdating}: EditRentalVehicleFormProps) => {
  const {formApi} = useVehicleFormContext<RentalVehicle>();

  return (
    <FullScreenModal
      headline={i18n.t('entity.vehicle.labels.editRentalVehicle')}
      backgroundColor="general.white"
      scrollable={false}
      actions={[
        {
          title: i18n.t('general.labels.discard'),
          variant: 'secondary',
          isLoading: isUpdating,
          onClick: onDiscard,
        },
        {
          title: i18n.t('general.actions.save'),
          isLoading: isUpdating,
          onClick: () => {
            formApi.handleSubmit(onSave, (error) => {
              if (process.env.ENV_TYPE === 'dev') {
                console.error(error);
              }
            })();
          },
        },
      ]}
    >
      <RentalInformation />
    </FullScreenModal>
  );
};

export function EditRentalVehicle() {
  const navigate = useNavigate();
  const {id} = useRequiredParams<{id: string}>();

  const {
    data: rentalVehicleData,
    isLoading: isLoadingRentalVehicle,
    isError: isRentalVehicleError,
  } = useGetRentalVehicleQuery({
    vehicleId: id,
  });

  const {
    data: vehicleData,
    isLoading: isLoadingVehicle,
    isError: isVehicleError,
  } = useGetVehicleQuery({
    vehicleId: id,
  });

  const isLoading = isLoadingVehicle || isLoadingRentalVehicle;
  const isError = isRentalVehicleError || isVehicleError;

  const [updateRentalVehicle, {isLoading: isUpdating}] = usePutRentalVehicleMutation();

  const handleSave = (values: RentalVehicle) =>
    updateRentalVehicle({
      vehicleId: id,
      rentalVehicleRequestBody: values.rentalProperties,
    })
      .unwrap()
      .then(() => {
        navigate(composePath(vehiclesRoutes.rental, {params: {id}}));
      })
      .catch(handleApiError);

  const defaultValues =
    rentalVehicleData && vehicleData
      ? {
          ...toBaseVehicle(vehicleData),
          rentalProperties: {
            carParkId: rentalVehicleData?.carParkId || '',
            availability: rentalVehicleData?.availability,
            note: rentalVehicleData?.note,
            equipment: rentalVehicleData?.equipment,
            rentalPriceGroup: rentalVehicleData?.rentalPriceGroup,
          },
        }
      : undefined;

  return (
    <>
      <Helmet>
        <title>
          {formatDocumentTitle(
            i18n.t('entity.vehicle.labels.editRentalVehicle'),
            i18n.t('entity.vehicle.labels.pageTitle')
          )}
        </title>
      </Helmet>

      <DataStatus isLoading={isLoading} isError={isError}>
        <VFKit.Form
          mode="onBlur"
          experimentalZodSchema={RentalVehicleValidationSchema}
          defaultValues={defaultValues}
        >
          <EditRentalVehicleForm
            onSave={handleSave}
            onDiscard={() => navigate(-1)}
            isUpdating={isUpdating}
          />
        </VFKit.Form>
      </DataStatus>
    </>
  );
}
